import { TableTheme } from "@omegagrid/grid";
import rp, { CodeEditorAdapter, CodeEditorOptions, parseTableThemeInput } from "@reporting/rp-app-host";
import { HistoryItem } from '@reporting/rp-types/core';
import { Style as DefStyle } from "@reporting/rp-types/definition";

/**
 * Raw JSON view of the table style editor. The text is the working theme of
 * the editor (read through a callback); the apply button parses it back into
 * the working copy and the live preview follows - the stored style is written
 * only by the editor's own save. The history dropdown of the code editor
 * shows (and diffs) the saved versions of the style.
 */
export class TableStyleCodeEditorAdapter extends CodeEditorAdapter {

	constructor(
		private style: DefStyle,
		private getTheme: () => TableTheme|null,
		/** text shown when there is no working theme (a stored style with broken JSON) */
		private getFallbackContent: () => string|null,
		private applyTheme: (theme: TableTheme) => void,
	) { super() }

	getContent(): string {
		const theme = this.getTheme();
		return theme ? JSON.stringify(theme, null, '\t') : (this.getFallbackContent() ?? '{}');
	}

	/**
	 * Parse and apply the text as the working theme. Returns false in every
	 * case: the change tracking runs through the events of the style editor,
	 * a save event of the code editor would mark the style as saved.
	 */
	save(content: string): boolean {
		const theme = parseTableThemeInput(content);
		if (theme) this.applyTheme(theme);
		return false;
	}

	getHistoryItems(): Promise<HistoryItem[]> {
		return rp.ms('definition').get<HistoryItem[]>(`styles/${this.style.id_style}/history`);
	}

	getHistoryContent(idz: number): Promise<string> {
		return rp.ms('definition').get<string>(`styles/${this.style.id_style}/history/${idz}`);
	}

	getCurrentItem(): HistoryItem {
		return this.style;
	}

	getOptions(): CodeEditorOptions {
		return this.createOptions({
			history: true,
			language: 'json',
			toolbar: {
				save: { text: '', icon: 'circle-check' },
				discard: { text: '', },
			},
		});
	}

}
